import React, { useState } from 'react';
import { callDialerService, CompleteCallData } from '../../services/callDialerService';
import { FiX, FiPhone, FiCheckCircle } from 'react-icons/fi';
import Icon from '../common/Icon';
import { useCallOutcomeOptions } from '../../hooks/useCallOutcomeOptions';
import CallStatusSelect from './CallStatusSelect';
import PriorCallAlert from './PriorCallAlert';

interface CallCompletionModalProps {
  callLogId: number;
  entityType: 'lead' | 'customer' | 'tender';
  entityId: number;
  entityName: string;
  phoneNumber: string;
  onComplete: () => void;
}

const CallCompletionModal: React.FC<CallCompletionModalProps> = ({
  callLogId,
  entityType,
  entityId,
  entityName,
  phoneNumber,
  onComplete
}) => {
  const { options: callOutcomeOptions, loading: callOutcomeOptionsLoading } = useCallOutcomeOptions();
  const [formData, setFormData] = useState<CompleteCallData>({
    call_log_id: callLogId,
    call_status_id: undefined,
    call_outcome: '',
    notes: '',
    call_duration_seconds: undefined,
    next_call_date: '',
    next_call_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      await callDialerService.complete(formData);
      setSuccess(true);
      setTimeout(() => {
        onComplete();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to complete call log');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
        <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-md shadow-xl rounded-lg bg-white">
          <div className="text-center py-4">
            <Icon component={FiCheckCircle} className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Call Logged Successfully!</h3>
            <p className="text-sm text-gray-600">Your call has been recorded.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
      <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-md shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
            <Icon component={FiPhone} className="w-5 h-5 text-green-600" />
            Complete Call Log
          </h3>
          <button
            onClick={onComplete}
            className="text-gray-400 hover:text-gray-600"
          >
            <Icon component={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Contact:</span> {entityName}
          </p>
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Phone:</span> {phoneNumber}
          </p>
        </div>

        <PriorCallAlert
          entityType={entityType}
          entityId={entityId}
          excludeCallLogId={callLogId}
        />

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Status *</label>
            <CallStatusSelect
              value={formData.call_status_id}
              onChange={(call_status_id) => setFormData({ ...formData, call_status_id })}
              required
              className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Outcome *</label>
            <select
              required
              value={formData.call_outcome || ''}
              onChange={(e) => setFormData({ ...formData, call_outcome: e.target.value })}
              disabled={callOutcomeOptionsLoading}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:opacity-60"
            >
              <option value="">{callOutcomeOptionsLoading ? 'Loading…' : 'Select Outcome'}</option>
              {callOutcomeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Duration (seconds)</label>
            <input
              type="number"
              min="0"
              value={formData.call_duration_seconds || ''}
              onChange={(e) => setFormData({ ...formData, call_duration_seconds: e.target.value ? parseInt(e.target.value) : undefined })}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Optional"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Call notes..."
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Date</label>
            <input
              type="datetime-local"
              value={formData.next_call_date || ''}
              onChange={(e) => setFormData({ ...formData, next_call_date: e.target.value })}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Notes</label>
            <textarea
              value={formData.next_call_notes || ''}
              onChange={(e) => setFormData({ ...formData, next_call_notes: e.target.value })}
              rows={2}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Notes for next call..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onComplete}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
            >
              Skip
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium disabled:opacity-50 font-bold"
            >
              {loading ? 'Saving...' : 'Save Call Log'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CallCompletionModal;




